/**
 * FlowMapPage — DRR-222 Tenant Flow Map Visualization
 *
 * Multi-view visualization of work streams flowing through the facility.
 * Three synchronized views: Sankey (flow), Chord (relationships), Temporal (time).
 *
 * Route: /ferp-core/flow-map
 * Nav group: admin (DRR-196)
 * Scope: analytics:view (DRR-102)
 * Data tier: T2 operational (DRR-114)
 *
 * Key features:
 * - Sankey: Sources → Performers → Subjects flow
 * - Chord: Staff-Resident relationship strength
 * - Temporal: Activity heatmap by hour
 * - Selection syncs across all views
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Alert,
  Badge,
  Box,
  Card,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconChartSankey,
  IconChartDonut,
  IconClock,
  IconUsers,
  IconUser,
  IconFileText,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import {
  useTenantFlowMap,
  FLOW_MAP_SOURCE_COLORS,
  FLOW_MAP_SOURCE_LABELS,
  FlowMapSourceType,
  type TenantFlowMap,
  type FlowMapSource,
  type FlowMapPerformer,
  type FlowMapSubject,
} from '@/hooks/useCoordination';

// Alias types for component props
type GqlFlowMap = TenantFlowMap;
type GqlSource = FlowMapSource;
type GqlPerformer = FlowMapPerformer;
type GqlSubject = FlowMapSubject;

// ---------------------------------------------------------------------------
// Date Range Selector
// ---------------------------------------------------------------------------

function DateRangeSelector({
  value,
  onChange,
}: {
  value: { start: Date; end: Date };
  onChange: (range: { start: Date; end: Date }) => void;
}) {
  return (
    <Group gap="xs">
      <DatePickerInput
        label="From"
        value={value.start}
        onChange={(d: string | null) => d && onChange({ ...value, start: new Date(d) })}
        size="sm"
        w={140}
      />
      <DatePickerInput
        label="To"
        value={value.end}
        onChange={(d: string | null) => d && onChange({ ...value, end: new Date(d) })}
        size="sm"
        w={140}
      />
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Flow Stats Summary
// ---------------------------------------------------------------------------

type StatType = 'tasks' | 'staff' | 'subjects' | 'intersections';

interface FlowStatsProps {
  flowMap: GqlFlowMap | null;
  loading: boolean;
  selectedStat: StatType | null;
  onSelectStat: (stat: StatType | null) => void;
}

function FlowStats({ flowMap, loading, selectedStat, onSelectStat }: FlowStatsProps) {
  if (loading) {
    return (
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={80} radius="md" />
        ))}
      </SimpleGrid>
    );
  }

  if (!flowMap) {
    return (
      <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
        No flow data available for the selected date range.
      </Alert>
    );
  }

  const totalTasks = flowMap.sources.reduce((sum, s) => sum + s.taskCount, 0);
  const performerCount = flowMap.performers.length;
  const subjectCount = flowMap.subjects.length;
  const intersectionCount = flowMap.intersections.length;

  const stats: { key: StatType; label: string; value: number; icon: typeof IconFileText; color: string }[] = [
    { key: 'tasks', label: 'Total Tasks', value: totalTasks, icon: IconFileText, color: 'blue' },
    { key: 'staff', label: 'Staff Active', value: performerCount, icon: IconUsers, color: 'violet' },
    { key: 'subjects', label: 'Subjects', value: subjectCount, icon: IconUser, color: 'green' },
    { key: 'intersections', label: 'Intersections', value: intersectionCount, icon: IconArrowRight, color: 'orange' },
  ];

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      {stats.map((stat) => {
        const isSelected = selectedStat === stat.key;

        return (
          <Card
            key={stat.key}
            withBorder
            padding="md"
            style={{
              cursor: 'pointer',
              backgroundColor: isSelected ? `var(--mantine-color-${stat.color}-0)` : undefined,
              borderColor: isSelected ? `var(--mantine-color-${stat.color}-5)` : undefined,
              borderWidth: isSelected ? 2 : 1,
            }}
            onClick={() => onSelectStat(isSelected ? null : stat.key)}
          >
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  {stat.label}
                </Text>
                <Text size="xl" fw={700}>
                  {stat.value}
                </Text>
              </div>
              <ThemeIcon color={stat.color} variant={isSelected ? 'filled' : 'light'} size="xl" radius="md">
                <stat.icon size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------------------
// Sankey View (Text-based for Phase 1)
// ---------------------------------------------------------------------------

interface SankeyViewProps {
  flowMap: GqlFlowMap;
  selectedActorId: string | null;
  selectedSourceId: string | null;
  onSelectActor: (id: string | null) => void;
  onSelectSource: (id: string | null) => void;
}

function SankeyView({ flowMap, selectedActorId, selectedSourceId, onSelectActor, onSelectSource }: SankeyViewProps) {
  // Group sources by type for display
  const sourcesByType = useMemo(() => {
    const map = new Map<FlowMapSourceType, GqlSource[]>();
    for (const source of flowMap.sources) {
      const type = source.sourceType as FlowMapSourceType;
      const list = map.get(type) ?? [];
      list.push(source);
      map.set(type, list);
    }
    return map;
  }, [flowMap.sources]);

  return (
    <SimpleGrid cols={3} spacing="md">
      {/* Sources Column */}
      <Card withBorder>
        <Text fw={600} mb="md">Sources</Text>
        <Stack gap="xs">
          {flowMap.sources.map((source) => {
            const isSelected = selectedSourceId === source.id;

            return (
              <Paper
                key={source.id}
                p="xs"
                withBorder
                style={{
                  borderLeftWidth: 4,
                  borderLeftColor: `var(--mantine-color-${FLOW_MAP_SOURCE_COLORS[source.sourceType as FlowMapSourceType]}-6)`,
                  cursor: 'pointer',
                  backgroundColor: isSelected ? `var(--mantine-color-${FLOW_MAP_SOURCE_COLORS[source.sourceType as FlowMapSourceType]}-0)` : undefined,
                  borderColor: isSelected ? `var(--mantine-color-${FLOW_MAP_SOURCE_COLORS[source.sourceType as FlowMapSourceType]}-5)` : undefined,
                }}
                onClick={() => onSelectSource(isSelected ? null : source.id)}
              >
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {source.label}
                    </Text>
                    <Badge size="xs" color={FLOW_MAP_SOURCE_COLORS[source.sourceType as FlowMapSourceType]}>
                      {FLOW_MAP_SOURCE_LABELS[source.sourceType as FlowMapSourceType]}
                    </Badge>
                  </div>
                  <Badge variant="light">{source.taskCount}</Badge>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Card>

      {/* Performers Column */}
      <Card withBorder>
        <Text fw={600} mb="md">Staff</Text>
        <Stack gap="xs">
          {flowMap.performers.map((performer) => {
            const totalTasks = performer.taskCountBySubject.reduce((sum, t) => sum + t.count, 0);
            const isSelected = selectedActorId === performer.actorId;

            return (
              <Paper
                key={performer.actorId}
                p="xs"
                withBorder
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
                  borderColor: isSelected ? 'var(--mantine-color-blue-5)' : undefined,
                }}
                onClick={() => onSelectActor(isSelected ? null : performer.actorId)}
              >
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>
                      {performer.displayName}
                    </Text>
                    <Badge size="xs" color="gray">{performer.role}</Badge>
                  </div>
                  <Badge variant="light">{totalTasks}</Badge>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Card>

      {/* Subjects Column */}
      <Card withBorder>
        <Text fw={600} mb="md">Subjects</Text>
        <Stack gap="xs">
          {flowMap.subjects.map((subject) => {
            const totalTasks = subject.taskCountByPerformer.reduce((sum, t) => sum + t.count, 0);
            const isHighlighted = selectedActorId
              ? subject.taskCountByPerformer.some((t) => t.performerId === selectedActorId)
              : false;

            return (
              <Paper
                key={subject.actorId}
                p="xs"
                withBorder
                style={{
                  opacity: selectedActorId && !isHighlighted ? 0.5 : 1,
                  backgroundColor: isHighlighted ? 'var(--mantine-color-green-0)' : undefined,
                }}
              >
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    {subject.displayName}
                  </Text>
                  <Badge variant="light">{totalTasks}</Badge>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Card>
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------------------
// Chord View (Matrix for Phase 1)
// ---------------------------------------------------------------------------

interface ChordViewProps {
  flowMap: GqlFlowMap;
  selectedActorId: string | null;
  onSelectActor: (id: string | null) => void;
}

function ChordView({ flowMap, selectedActorId, onSelectActor }: ChordViewProps) {
  // Build relationship matrix: performer → subject → count
  const matrix = useMemo(() => {
    const result: { performerId: string; performerName: string; subjects: { subjectId: string; subjectName: string; count: number }[] }[] = [];

    for (const performer of flowMap.performers) {
      const subjects = performer.taskCountBySubject.map((t) => {
        const subject = flowMap.subjects.find((s) => s.actorId === t.subjectId);
        return {
          subjectId: t.subjectId,
          subjectName: subject?.displayName ?? t.subjectId,
          count: t.count,
        };
      });
      result.push({
        performerId: performer.actorId,
        performerName: performer.displayName,
        subjects,
      });
    }
    return result;
  }, [flowMap]);

  return (
    <Card withBorder>
      <Text fw={600} mb="md">Staff-Subject Relationships</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Staff</Table.Th>
            {flowMap.subjects.map((s) => (
              <Table.Th key={s.actorId} ta="center">
                <Text size="xs" lineClamp={1}>{s.displayName.split(' ')[0]}</Text>
              </Table.Th>
            ))}
            <Table.Th ta="center">Total</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {matrix.map((row) => {
            const isSelected = selectedActorId === row.performerId;
            const total = row.subjects.reduce((sum, s) => sum + s.count, 0);

            return (
              <Table.Tr
                key={row.performerId}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
                }}
                onClick={() => onSelectActor(isSelected ? null : row.performerId)}
              >
                <Table.Td>
                  <Text size="sm" fw={500}>{row.performerName}</Text>
                </Table.Td>
                {flowMap.subjects.map((subject) => {
                  const match = row.subjects.find((s) => s.subjectId === subject.actorId);
                  const count = match?.count ?? 0;
                  const intensity = count > 0 ? Math.min(count / 30, 1) : 0;

                  return (
                    <Table.Td
                      key={subject.actorId}
                      ta="center"
                      style={{
                        backgroundColor: count > 0
                          ? `rgba(34, 139, 230, ${intensity * 0.3})`
                          : undefined,
                      }}
                    >
                      {count > 0 ? count : '-'}
                    </Table.Td>
                  );
                })}
                <Table.Td ta="center" fw={600}>{total}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Temporal Heatmap View
// ---------------------------------------------------------------------------

interface TemporalViewProps {
  flowMap: GqlFlowMap;
  selectedActorId: string | null;
  onSelectActor: (id: string | null) => void;
}

function TemporalView({ flowMap, selectedActorId, onSelectActor }: TemporalViewProps) {
  const hours = [6, 9, 12, 15, 18, 21];
  const hourLabels = ['6am', '9am', '12pm', '3pm', '6pm', '9pm'];

  // Group buckets by actor
  const actorBuckets = useMemo(() => {
    const map = new Map<string, Map<number, { taskCount: number; intersectionCount: number }>>();

    for (const bucket of flowMap.activityBuckets) {
      if (!map.has(bucket.actorId)) {
        map.set(bucket.actorId, new Map());
      }
      map.get(bucket.actorId)!.set(bucket.hourOfDay, {
        taskCount: bucket.taskCount,
        intersectionCount: bucket.intersectionCount,
      });
    }
    return map;
  }, [flowMap.activityBuckets]);

  // Get max task count for intensity scaling
  const maxTasks = useMemo(() => {
    return Math.max(...flowMap.activityBuckets.map((b) => b.taskCount), 1);
  }, [flowMap.activityBuckets]);

  return (
    <Card withBorder>
      <Text fw={600} mb="md">Activity by Hour</Text>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Actor</Table.Th>
            {hourLabels.map((label, i) => (
              <Table.Th key={hours[i]} ta="center">{label}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {/* Staff rows */}
          {flowMap.performers.map((performer) => {
            const buckets = actorBuckets.get(performer.actorId);
            const isSelected = selectedActorId === performer.actorId;

            return (
              <Table.Tr
                key={performer.actorId}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
                }}
                onClick={() => onSelectActor(isSelected ? null : performer.actorId)}
              >
                <Table.Td>
                  <Group gap="xs">
                    <ThemeIcon size="xs" color="blue" variant="light">
                      <IconUser size={12} />
                    </ThemeIcon>
                    <Text size="sm">{performer.displayName}</Text>
                  </Group>
                </Table.Td>
                {hours.map((hour) => {
                  const data = buckets?.get(hour);
                  const intensity = data ? data.taskCount / maxTasks : 0;
                  const hasIntersection = data && data.intersectionCount > 0;

                  return (
                    <Table.Td
                      key={hour}
                      ta="center"
                      style={{
                        backgroundColor: intensity > 0
                          ? `rgba(34, 139, 230, ${intensity * 0.5})`
                          : 'var(--mantine-color-gray-1)',
                      }}
                    >
                      <Tooltip
                        label={data ? `${data.taskCount} tasks, ${data.intersectionCount} shared` : 'No activity'}
                        disabled={!data}
                      >
                        <Text size="xs" fw={intensity > 0.5 ? 600 : 400}>
                          {data?.taskCount ?? '-'}
                          {hasIntersection && <span style={{ color: 'orange' }}> *</span>}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed" mt="sm">
        * indicates tasks shared with other staff (intersections)
      </Text>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function FlowMapPage() {
  const tenantId = useTenantId() ?? '';

  // Default to last 7 days
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return { start, end };
  });

  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [selectedStat, setSelectedStat] = useState<StatType | null>(null);

  const { flowMap, loading, error } = useTenantFlowMap(tenantId, dateRange);

  const handleSelectActor = useCallback((id: string | null) => {
    setSelectedActorId(id);
  }, []);

  const handleSelectSource = useCallback((id: string | null) => {
    setSelectedSourceId(id);
  }, []);

  const handleSelectStat = useCallback((stat: StatType | null) => {
    setSelectedStat(stat);
  }, []);

  return (
    <Stack gap="lg" p="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Tenant Flow Map</Title>
          <Text c="dimmed" size="sm">
            DRR-222 Work Stream Visualization
          </Text>
        </div>

        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </Group>

      {/* Stats */}
      <FlowStats
        flowMap={flowMap}
        loading={loading}
        selectedStat={selectedStat}
        onSelectStat={handleSelectStat}
      />

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Failed to load flow map data: {error.message}
        </Alert>
      )}

      <Divider />

      {/* Main content tabs */}
      {flowMap && (
        <Tabs defaultValue="sankey">
          <Tabs.List>
            <Tabs.Tab value="sankey" leftSection={<IconChartSankey size={14} />}>
              Flow (Sankey)
            </Tabs.Tab>
            <Tabs.Tab value="chord" leftSection={<IconChartDonut size={14} />}>
              Relationships (Chord)
            </Tabs.Tab>
            <Tabs.Tab value="temporal" leftSection={<IconClock size={14} />}>
              Temporal Heatmap
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="sankey" pt="md">
            <SankeyView
              flowMap={flowMap}
              selectedActorId={selectedActorId}
              selectedSourceId={selectedSourceId}
              onSelectActor={handleSelectActor}
              onSelectSource={handleSelectSource}
            />
          </Tabs.Panel>

          <Tabs.Panel value="chord" pt="md">
            <ChordView
              flowMap={flowMap}
              selectedActorId={selectedActorId}
              onSelectActor={handleSelectActor}
            />
          </Tabs.Panel>

          <Tabs.Panel value="temporal" pt="md">
            <TemporalView
              flowMap={flowMap}
              selectedActorId={selectedActorId}
              onSelectActor={handleSelectActor}
            />
          </Tabs.Panel>
        </Tabs>
      )}

      {!flowMap && !loading && (
        <Card withBorder p="xl">
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" color="gray" variant="light">
              <IconChartSankey size={32} />
            </ThemeIcon>
            <Text c="dimmed">
              Select a date range to visualize work flow through your facility.
            </Text>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
